import React from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import BackToTop from '../components/layout/BackToTop';
import GroupInterestForm from '../components/forms/GroupInterestForm';
import { Button } from '../components/ui/button';
import { usePageMeta } from '../lib/usePageMeta';
import d from '../content/resident-groups/index.json';
import * as Icons from 'lucide-react';
import { CheckCircle, ArrowRight, ArrowDown, Users, Heart, Quote } from 'lucide-react';

const getIcon = (name) => {
  const Icon = Icons[name];
  return Icon ? <Icon className="w-5 h-5" /> : null;
};

const KeyMessage = ({ children, testid }) => (
  <div className="bg-[#1F1E1E] rounded-2xl p-6 sm:p-8 text-center" data-testid={testid}>
    <p className="text-white font-semibold text-base sm:text-lg leading-relaxed max-w-2xl mx-auto">{children}</p>
  </div>
);

export default function ResidentGroupsPage() {
  usePageMeta(
    'For TRAs & Community Groups — ResiAX',
    'ResiAX: Community helps TRAs and resident-led groups organise their work, hear from more of the community with less admin, and keep a clearer record for formal engagement.'
  );

  React.useEffect(() => {
    if (window.location.hash) {
      setTimeout(() => {
        document.querySelector(window.location.hash)?.scrollIntoView({ behavior: 'smooth' });
      }, 300);
    } else {
      window.scrollTo(0, 0);
    }
  }, []);

  const scrollToTalk = () => document.querySelector('#talk')?.scrollIntoView({ behavior: 'smooth' });

  return (
    <div className="min-h-screen bg-[#FFFAF8]">
      <Header />
      <main>
        {/* Hero */}
        <section className="pt-32 pb-14 bg-white" data-testid="groups-hero">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="inline-flex items-center gap-2 text-xs uppercase tracking-widest font-bold text-[#B5541B] mb-4">
              <span className="w-8 h-px bg-[#B5541B]" />
              {d.hero.eyebrow}
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-[#1F1E1E] leading-tight mb-5" data-testid="groups-heading">
              {d.hero.title}
            </h1>
            <p className="text-sm font-semibold text-[#3B3535]/80 mb-4 max-w-3xl">{d.hero.qualifier}</p>
            <p className="text-base md:text-lg text-[#3B3535]/75 leading-relaxed max-w-3xl mb-8">{d.hero.intro}</p>
            <div className="flex flex-wrap gap-4">
              <Button
                onClick={scrollToTalk}
                data-testid="groups-hero-cta"
                className="bg-[#B5541B] hover:bg-[#8B3F14] text-white font-semibold h-auto px-7 py-5 rounded-full shadow-lg hover:shadow-xl transition-all duration-300"
              >
                {d.hero.primaryCta}
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
              <Button
                asChild
                variant="outline"
                data-testid="groups-residents-cta"
                className="border-2 border-[#B5541B] text-[#B5541B] hover:bg-[#B5541B]/10 font-semibold h-auto px-6 py-5 rounded-full"
              >
                <Link to="/residents">{d.hero.secondaryCta}</Link>
              </Button>
            </div>
          </div>
        </section>

        {/* Central proposition */}
        <section className="py-14 bg-[#FFF8F5]" data-testid="groups-proposition">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-2xl sm:text-3xl font-bold text-[#B5541B] mb-3">{d.proposition.keyMessage}</h2>
            <p className="text-base font-semibold text-[#1F1E1E] mb-5 max-w-2xl mx-auto">{d.proposition.supporting}</p>
            <p className="text-sm text-[#3B3535]/70 leading-relaxed max-w-3xl mx-auto">{d.proposition.body}</p>
          </div>
        </section>

        {/* Benefit 1: participation */}
        <section className="py-16 bg-white" data-testid="groups-participation">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl sm:text-3xl font-bold text-[#1F1E1E] mb-3">{d.participation.heading}</h2>
            <p className="text-base text-[#3B3535]/75 leading-relaxed max-w-3xl mb-6">{d.participation.lead}</p>
            <div className="flex flex-wrap gap-2.5 mb-5">
              {d.participation.levels.map((l, i) => (
                <span key={i} className="inline-flex items-center gap-2 bg-[#FFF8F5] border border-[#B5541B]/10 rounded-full px-4 py-2 text-sm text-[#3B3535]/85">
                  <CheckCircle className="w-3.5 h-3.5 text-[#B5541B]" />
                  {l}
                </span>
              ))}
            </div>
            <p className="text-sm text-[#3B3535]/70 leading-relaxed max-w-3xl mb-8">{d.participation.widerNote}</p>
            <div className="bg-[#FFF8F5] rounded-3xl p-7 sm:p-8 border border-[#B5541B]/10" data-testid="groups-example">
              <p className="text-xs font-bold uppercase tracking-wide text-[#B5541B] mb-2">{d.participation.exampleHeading}</p>
              <p className="text-sm text-[#3B3535]/85 mb-5">{d.participation.exampleIntro}</p>
              <ol className="space-y-2.5 mb-5">
                {d.participation.exampleSteps.map((step, i) => (
                  <li key={i} className="flex items-start gap-3 text-sm text-[#3B3535]/85">
                    <span className="w-6 h-6 rounded-full bg-[#B5541B] text-white text-xs font-bold flex items-center justify-center shrink-0">{i + 1}</span>
                    {step}
                  </li>
                ))}
              </ol>
              <p className="text-sm font-semibold text-[#1F1E1E]">{d.participation.exampleOutro}</p>
            </div>
          </div>
        </section>

        {/* Benefit 2: representation */}
        <section className="py-16 bg-[#FFF8F5]" data-testid="groups-representation">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl sm:text-3xl font-bold text-[#1F1E1E] mb-3">{d.representation.heading}</h2>
            <p className="text-base text-[#3B3535]/75 leading-relaxed max-w-3xl mb-6">{d.representation.lead}</p>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-8">
              {d.representation.gathers.map((g, i) => (
                <div key={i} className="flex items-start gap-2.5 text-sm text-[#3B3535]/85 bg-white rounded-xl p-4 border border-gray-100">
                  <CheckCircle className="w-4 h-4 text-[#B5541B] mt-0.5 shrink-0" />
                  {g}
                </div>
              ))}
            </div>
            <KeyMessage testid="groups-representation-message">{d.representation.keyMessage}</KeyMessage>
            <p className="text-sm text-[#3B3535]/70 leading-relaxed max-w-3xl mt-8 mb-4">{d.representation.chainIntro}</p>
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2" data-testid="groups-chain">
              {d.representation.chain.map((step, i) => (
                <React.Fragment key={step}>
                  <div className="flex-1 bg-white border border-[#B5541B]/20 rounded-xl px-4 py-3.5 text-center text-sm font-bold text-[#1F1E1E]">
                    {step}
                  </div>
                  {i < d.representation.chain.length - 1 && (
                    <>
                      <ArrowRight className="w-4 h-4 text-[#B5541B] shrink-0 hidden sm:block mx-auto" />
                      <ArrowDown className="w-4 h-4 text-[#B5541B] shrink-0 sm:hidden mx-auto" />
                    </>
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>
        </section>

        {/* Benefit 3: formal record */}
        <section className="py-16 bg-white" data-testid="groups-formal-record">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl sm:text-3xl font-bold text-[#1F1E1E] mb-3">{d.formalRecord.heading}</h2>
            <p className="text-base text-[#3B3535]/75 leading-relaxed max-w-3xl mb-6">{d.formalRecord.lead}</p>
            <p className="text-sm font-semibold text-[#3B3535]/80 mb-3">It helps turn activity from different channels into a clearer, more durable record when something needs to become:</p>
            <div className="flex flex-wrap gap-2.5 mb-8">
              {d.formalRecord.becomes.map((b, i) => (
                <span key={i} className="inline-flex items-center gap-2 bg-[#FFF8F5] border border-[#B5541B]/10 rounded-full px-4 py-2 text-sm text-[#3B3535]/85">
                  <CheckCircle className="w-3.5 h-3.5 text-[#B5541B]" />
                  {b}
                </span>
              ))}
            </div>
            <KeyMessage testid="groups-formal-message">{d.formalRecord.keyMessage}</KeyMessage>
            <p className="text-sm text-[#3B3535]/60 italic mt-5 text-center">{d.formalRecord.note}</p>
          </div>
        </section>

        {/* Engagement spectrum */}
        <section className="py-16 bg-[#FFF8F5]" data-testid="groups-spectrum">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-10">
              <h2 className="text-2xl sm:text-3xl font-bold text-[#1F1E1E] mb-3">{d.spectrum.heading}</h2>
              <p className="text-sm text-[#3B3535]/70 max-w-2xl mx-auto">{d.spectrum.description}</p>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-8">
              {d.spectrum.levels.map((level, i) => (
                <div key={i} data-testid={`spectrum-level-${i}`} className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm">
                  <div className="w-10 h-10 rounded-xl bg-[#B5541B]/10 flex items-center justify-center text-[#B5541B] mb-4">
                    {getIcon(level.icon)}
                  </div>
                  <h3 className="font-bold text-[#1F1E1E] text-sm mb-1.5">{level.title}</h3>
                  <p className="text-xs text-[#3B3535]/70 leading-relaxed">{level.description}</p>
                </div>
              ))}
            </div>
            <p className="text-center font-semibold text-[#B5541B]" data-testid="groups-spectrum-line">{d.spectrum.supporting}</p>
          </div>
        </section>

        {/* Membership ≠ voice */}
        <section className="py-16 bg-white" data-testid="groups-membership">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl sm:text-3xl font-bold text-[#1F1E1E] mb-5">{d.membership.heading}</h2>
            <div className="border-l-4 border-[#B5541B] bg-[#FFF8F5] rounded-r-xl p-5 mb-8">
              <Quote className="w-5 h-5 text-[#B5541B]/40 mb-1.5" />
              <p className="font-semibold text-[#1F1E1E]">{d.membership.principle}</p>
            </div>
            <div className="grid sm:grid-cols-2 gap-4 mb-6">
              {d.membership.definitions.map((def) => (
                <div key={def.term} className="bg-[#FFF8F5] rounded-xl p-5 border border-[#B5541B]/10">
                  <h3 className="font-bold text-[#B5541B] text-sm uppercase tracking-wide mb-1">{def.term}</h3>
                  <p className="text-sm text-[#3B3535]/80">{def.meaning}</p>
                </div>
              ))}
            </div>
            {d.membership.notes.map((n, i) => (
              <p key={i} className="text-sm text-[#3B3535]/70 leading-relaxed mb-2.5">{n}</p>
            ))}
          </div>
        </section>

        {/* Community vs Support Circle */}
        <section className="py-16 bg-[#FFF8F5]" data-testid="groups-circle-distinction">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl sm:text-3xl font-bold text-[#1F1E1E] mb-8 text-center">{d.circleDistinction.heading}</h2>
            <div className="grid md:grid-cols-2 gap-5 mb-8">
              <div className="bg-white rounded-2xl p-7 border border-gray-100 shadow-sm">
                <div className="w-10 h-10 rounded-xl bg-[#B5541B]/10 flex items-center justify-center text-[#B5541B] mb-4">
                  <Users className="w-5 h-5" />
                </div>
                <h3 className="font-bold text-[#1F1E1E] mb-3">{d.circleDistinction.community.title}</h3>
                <ul className="space-y-2">
                  {d.circleDistinction.community.items.map((item, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-[#3B3535]/80">
                      <CheckCircle className="w-3.5 h-3.5 text-[#B5541B] mt-0.5 shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="bg-white rounded-2xl p-7 border border-gray-100 shadow-sm">
                <div className="w-10 h-10 rounded-xl bg-[#B5541B]/10 flex items-center justify-center text-[#B5541B] mb-4">
                  <Heart className="w-5 h-5" />
                </div>
                <h3 className="font-bold text-[#1F1E1E] mb-3">{d.circleDistinction.circle.title}</h3>
                <ul className="space-y-2 mb-4">
                  {d.circleDistinction.circle.items.map((item, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-[#3B3535]/80">
                      <CheckCircle className="w-3.5 h-3.5 text-[#B5541B] mt-0.5 shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
                <Link to="/residents#support" data-testid="groups-support-link" className="text-sm font-semibold text-[#B5541B] hover:text-[#8B3F14] transition-colors">
                  How Support Circle works →
                </Link>
              </div>
            </div>
            <KeyMessage testid="groups-circle-message">{d.circleDistinction.keyMessage}</KeyMessage>
            <p className="text-sm text-[#3B3535]/70 mt-5 text-center">{d.circleDistinction.note}</p>
          </div>
        </section>

        {/* Independence */}
        <section className="py-16 bg-white" data-testid="groups-independence">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl sm:text-3xl font-bold text-[#1F1E1E] mb-4">{d.independence.heading}</h2>
            <p className="text-base text-[#3B3535]/75 leading-relaxed mb-6">{d.independence.lead}</p>
            <div className="border-l-4 border-[#B5541B] bg-[#FFF8F5] rounded-r-xl p-5 mb-4">
              <p className="font-semibold text-[#1F1E1E]">{d.independence.principle}</p>
            </div>
            <p className="text-sm font-semibold text-[#3B3535]/80">{d.independence.closing}</p>
          </div>
        </section>

        {/* Conversation CTA + form */}
        <section id="talk" className="py-16 bg-[#FFF8F5] scroll-mt-20" data-testid="groups-talk">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-8">
              <h2 className="text-2xl sm:text-3xl font-bold text-[#1F1E1E] mb-4">{d.talk.heading}</h2>
              <p className="text-sm text-[#3B3535]/75 leading-relaxed max-w-2xl mx-auto">{d.talk.body}</p>
            </div>
            <GroupInterestForm />
            <p className="text-xs text-[#3B3535]/55 leading-relaxed text-center mt-8 max-w-2xl mx-auto" data-testid="groups-status-note">
              {d.statusNote}
            </p>
          </div>
        </section>
      </main>
      <Footer />
      <BackToTop />
    </div>
  );
}
